import React, { useState, useEffect, useCallback } from 'react';
import '../IpPatientDetails.css';
import {
  Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, FormGroup, Grid, IconButton, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { CommonDeleteButton, CommonEditButton, DeletePopup } from '../../../../../Components/Common Components/CommonComponents';
import { getCachevalue, localGetItem } from '../../../../../Utility/Services/CacheProviderService';
import RestAPIService from '../../../../../Utility/Services/RestAPIService';
import { Serviceurls } from '../../../../../Utility/API/Serviceurls';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import ToastMsg from '../../../../../Components/ToastMsg/ToastMsg';
import Loader from '../../../../../Components/Loader';

const FREQUENCY_OPTIONS = [1, 2, 3, 4, 5, 6, 8, 12];

function VitalByTask({ patientData: propPatientData, withOutAppointment, nurseDoctorId, isFromDoctorModule }) {
  const { t } = useTranslation();
  const [taskList, setTaskList] = useState([]);
  const [stationList, setStationList] = useState([]);
  const [patientStation, setPatientStation] = useState(null);
  const [vitalFields, setVitalFields] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [expandedTaskIds, setExpandedTaskIds] = useState({});
  const [deletePopup, setDeletePopup] = useState(false);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  const [isLoader, setIsLoader] = useState(false);
  const [isErrorMsg, setIsErrorMsg] = useState(false);
  const [isErrorMsgText, setIsErrorMsgText] = useState('');
  const [successMsg, setSuccessMsg] = useState(false);
  const [successMsgText, setSuccessMsgText] = useState('');
  const [selectedVitals, setSelectedVitals] = useState({});
  const [stationId, setStationId] = useState('');
  const [nurseId, setNurseId] = useState('');
  const [startDate, setStartDate] = useState(DateTime.now().toFormat('yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(DateTime.now().toFormat('yyyy-MM-dd'));
  const [startTime, setStartTime] = useState('08:00:00');
  const [endTime, setEndTime] = useState('20:00:00');
  const [frequency, setFrequency] = useState(4);
  const [instructions, setInstructions] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const getIpAdmissionId = useCallback(() => {
    return withOutAppointment?.ip_admission_id ?? propPatientData?.id ?? null;
  }, [withOutAppointment, propPatientData]);

  const getDoctorId = useCallback(() => {
    if (nurseDoctorId) return nurseDoctorId;
    const user = localGetItem('loggedInUserInfo');
    try {
      const parsed = user ? JSON.parse(user) : null;
      return parsed?.doctor_id ?? null;
    } catch (e) { return null; }
  }, [nurseDoctorId]);

  const fetchVitalConfig = useCallback(() => {
    let patientData = getCachevalue('patientData') || getCachevalue('IppatientData');
    let parsed = null;
    const clinicID = JSON.parse(localGetItem('SelectedDoctorClinicId'));
    const clinic_id = clinicID ?? null;
    try { parsed = patientData ? JSON.parse(patientData) : null; } catch (e) {}
    const specialization_id = parsed?.speciality_id ?? parsed?.specialization_id ?? '';
    setIsLoader(true);
    RestAPIService.getAll(`${Serviceurls.VITALS_CONFIGURATION}?specialization_id=${specialization_id}&clinic_id=${clinic_id}`)
      .then((res) => {
        if (res.data?.status === 'success' && res.data?.data?.[0]?.vital_layout_data) {
          const fields = (res.data.data[0].vital_layout_data || []).filter((f) => f?.is_active);
          setVitalFields(fields.map((f) => ({ field_name: f.field_name, label: f.label || f.field_name })));
        } else setVitalFields([]);
        setIsLoader(false);
      })
      .catch(() => {
        setVitalFields([]);
        setIsLoader(false);
      });
  }, []);

  const fetchStationList = useCallback(() => {
    const ip_admission_id = getIpAdmissionId();
    if (!ip_admission_id) return;
    RestAPIService.getAll(`${Serviceurls.IP_STATION_NURSE_LIST}?ip_admission_id=${ip_admission_id}`)
      .then((res) => {
        if (res.data?.status !== 'success') return;
        const data = res.data?.data || [];
        const patient_station = res.data?.patient_station || null;
        const defaultStation = patient_station || (data && data[0]) || null;
        const defaultNurse = defaultStation?.data?.[0] ?? null;
        setStationList(data);
        setPatientStation(patient_station);
        setStationId(defaultStation?.station_id ?? '');
        setNurseId(defaultNurse?.nurse_id ?? '');
      })
      .catch(() => {});
  }, [getIpAdmissionId]);

  const fetchTaskList = useCallback(() => {
    const ip_admission_id = getIpAdmissionId();
    if (!ip_admission_id) return;
    setIsLoader(true);
    RestAPIService.getAll(`${Serviceurls.IP_NURSE_VITAL_TASK}?ip_admission_id=${ip_admission_id}`)
      .then((res) => {
        if (res.data?.status === 'success') {
          setTaskList(res.data?.data || []);
        } else setTaskList([]);
        setIsLoader(false);
      })
      .catch(() => {
        setTaskList([]);
        setIsLoader(false);
      });
  }, [getIpAdmissionId]);

  useEffect(() => {
    fetchVitalConfig();
  }, [fetchVitalConfig]);

  useEffect(() => {
    const id = getIpAdmissionId();
    if (id) {
      fetchStationList();
      fetchTaskList();
    }
  }, [getIpAdmissionId, fetchStationList, fetchTaskList, withOutAppointment?.ip_admission_id, propPatientData?.id]);

  const openAddModal = () => {
    const defaultStation = patientStation || stationList[0];
    const defaultNurse = defaultStation?.data?.[0];
    setAddModalOpen(true);
    setSelectedVitals({});
    setStationId(defaultStation?.station_id ?? '');
    setNurseId(defaultNurse?.nurse_id ?? '');
    setStartDate(DateTime.now().toFormat('yyyy-MM-dd'));
    setEndDate(DateTime.now().toFormat('yyyy-MM-dd'));
    setStartTime('08:00:00');
    setEndTime('20:00:00');
    setFrequency(4);
    setInstructions('');
    setFormErrors({});
  };

  const closeAddModal = () => {
    setAddModalOpen(false);
    setFormErrors({});
  };

  const openEditModal = (task) => {
    const selected = {};
    (task.field_names || []).forEach((f) => { selected[f.field_name] = true; });
    setEditModalOpen(true);
    setEditingTask(task);
    setSelectedVitals(selected);
    setFormErrors({});
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingTask(null);
    setFormErrors({});
  };

  const toggleVital = (fieldName) => {
    setSelectedVitals((prev) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  const showError = (msg) => {
    setIsErrorMsg(true);
    setIsErrorMsgText(msg);
  };
  const showSuccess = (msg) => {
    setSuccessMsg(true);
    setSuccessMsgText(msg);
  };
  const msgClose = () => {
    setIsErrorMsg(false);
    setSuccessMsg(false);
  };

  const validateAddForm = () => {
    const selected = Object.keys(selectedVitals).filter((k) => selectedVitals[k]);
    const err = {};
    if (!selected.length) err.vitals = 'Select at least one vital';
    if (!startDate) err.startDate = 'Required';
    if (!endDate) err.endDate = 'Required';
    if (!startTime) err.startTime = 'Required';
    if (!endTime) err.endTime = 'Required';
    if (!stationId) err.stationId = 'Select nurse station';
    if (new Date(endDate) < new Date(startDate)) err.endDate = 'End date must be on or after start date';
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleAddTask = () => {
    if (!validateAddForm()) return;
    const ip_admission_id = getIpAdmissionId();
    if (!ip_admission_id) {
      showError('Admission or doctor not found.');
      return;
    }
    const field_names = vitalFields
      .filter((f) => selectedVitals[f.field_name])
      .map((f) => ({ field_name: f.field_name, label: f.label || f.field_name }));
    const payload = {
      frequency,
      start_time: startTime,
      end_time: endTime,
      start_date: startDate,
      end_date: endDate,
      station_id: Number(stationId),
      nurse_id: nurseId ? Number(nurseId) : null,
      ip_admission_id,
      instructions: (instructions || '').slice(0, 50),
      field_names,
    };
    setIsLoader(true);
    RestAPIService.create(payload, Serviceurls.IP_NURSE_VITAL_TASK)
      .then((res) => {
        setIsLoader(false);
        if (res.data?.status === 'success') {
          showSuccess(res.data?.message || 'Task added.');
          closeAddModal();
          fetchTaskList();
        } else showError(res.data?.message || 'Failed to add task.');
      })
      .catch((err) => {
        setIsLoader(false);
        showError(err?.response?.data?.message || err?.message || 'Failed to add task.');
      });
  };

  const validateEditForm = () => {
    const selected = Object.keys(selectedVitals).filter((k) => selectedVitals[k]);
    const err = {};
    if (!selected.length) err.vitals = 'Select at least one vital';
    setFormErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleEditTask = () => {
    if (!validateEditForm()) return;
    if (!editingTask?.id) return;
    const field_names = vitalFields
      .filter((f) => selectedVitals[f.field_name])
      .map((f) => ({ field_name: f.field_name, label: f.label || f.field_name }));
    const payload = { id: editingTask.id, field_names };
    setIsLoader(true);
    RestAPIService.updateWithOutId(payload, Serviceurls.IP_NURSE_VITAL_TASK)
      .then((res) => {
        setIsLoader(false);
        if (res.data?.status === 'success') {
          showSuccess(res.data?.message || 'Task updated.');
          closeEditModal();
          fetchTaskList();
        } else showError(res.data?.message || 'Failed to update.');
      })
      .catch((err) => {
        setIsLoader(false);
        showError(err?.response?.data?.message || err?.message || 'Failed to update.');
      });
  };

  const openDeletePopup = (id) => {
    setDeletePopup(true);
    setDeleteTaskId(id);
  };
  const closeDeletePopup = () => {
    setDeletePopup(false);
    setDeleteTaskId(null);
  };

  const handleDeleteTask = () => {
    if (!deleteTaskId) return;
    setIsLoader(true);
    RestAPIService.delete(`${Serviceurls.IP_NURSE_VITAL_TASK}?id=${deleteTaskId}`, {})
      .then((res) => {
        setIsLoader(false);
        closeDeletePopup();
        showSuccess(res?.data?.message || 'Task deleted.');
        fetchTaskList();
      })
      .catch((err) => {
        setIsLoader(false);
        showError(err?.response?.data?.message || err?.message || 'Failed to delete.');
      });
  };

  const toggleExpand = (id) => {
    setExpandedTaskIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const currentStation = stationList.find((s) => Number(s.station_id) === Number(stationId));
  const nurses = currentStation?.data || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
      {isFromDoctorModule && (
        <Button variant="contained" color="primary" size="small" onClick={openAddModal}>ADD TASK</Button>
        )}
      </Box>
      <Paper elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40 }} />
              <TableCell>Task Date &amp; Time</TableCell>
              <TableCell>Vitals recorded</TableCell>
              <TableCell>Internal</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Nurse</TableCell>
              {isFromDoctorModule && (<TableCell align="center">Action</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {taskList.map((row) => {
              const expanded = expandedTaskIds[row.id];
              const vitalsRecorded = (row.field_names || []).map((f) => f.label || f.field_name).join(', ');
              return (
                <React.Fragment key={row.id}>
                  <TableRow sx={{ fontSize: '0.875vw', fontWeight: 'bold'}}>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleExpand(row.id)}>
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{row.start_date} | {row.start_time}</TableCell>
                    <TableCell>{vitalsRecorded || '-'}</TableCell>
                    <TableCell>{row.frequency ?? '-'}</TableCell>
                    <TableCell>{row.status ?? '-'}</TableCell>
                    <TableCell>{row.nurse_name ?? '-'}</TableCell>
                    {isFromDoctorModule && (
                    <TableCell align="center">
                     
                          <CommonEditButton size="1.5vw" onClick={() => openEditModal(row)} />
                          <CommonDeleteButton size="1.5vw" onClick={() => openDeletePopup(row.id)} />
                    
                     
                    </TableCell>  )}
                  </TableRow>
                  {expanded && (row.vital_records || []).length > 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 1, px: 1, backgroundColor: 'action.hover', verticalAlign: 'top' }}>
                        <Table size="small" sx={{ minWidth: 400 }}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ py: 1, minWidth: 140 }}>Date &amp; Time</TableCell>
                              {(row.field_names || []).map((f) => (
                                <TableCell key={f.field_name} sx={{ py: 1.5, minWidth: 100 }}>{f.label || f.field_name}</TableCell>
                              ))}
                              <TableCell sx={{ py: 1.5, minWidth: 80 }}>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {row.vital_records.map((rec) => (
                              <TableRow key={rec.id}>
                                <TableCell sx={{ py: 1.5 }}>{rec.scheduled_time || '-'}</TableCell>
                                {(row.field_names || []).map((f) => {
                                  const v = (rec.vitals || []).find((x) => x.field_name === f.field_name);
                                  return <TableCell key={f.field_name} sx={{ py: 1.5 }}>{v?.value || '-'}</TableCell>;
                                })}
                                <TableCell sx={{ py: 1.5 }}>{rec.status ?? '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
        {taskList.length === 0 && (
          <Box sx={{ py: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">{t('NoRecordsFound')}</Typography>
          </Box>
        )}
      </Paper>

      <Dialog open={addModalOpen} onClose={closeAddModal} maxWidth="sm" fullWidth>
        <DialogTitle>Add Task</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Select Vitals</Typography>
          <FormGroup row sx={{ flexWrap: 'wrap' }}>
            {vitalFields.map((f) => (
              <FormControlLabel
                key={f.field_name}
                control={
                  <Checkbox
                    checked={!!selectedVitals[f.field_name]}
                    onChange={() => toggleVital(f.field_name)}
                  />
                }
                label={f.label || f.field_name}
              />
            ))}
          </FormGroup>
          {formErrors.vitals && <Typography color="error" variant="caption">{formErrors.vitals}</Typography>}

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5 }}>Task Details</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Select Nurse Station</Typography>
              <Select
                fullWidth
                size="small"
                value={stationId}
                onChange={(e) => { setStationId(e.target.value); setNurseId(''); }}
                displayEmpty
              >
                <MenuItem value="">Select</MenuItem>
                {stationList.map((s) => (
                  <MenuItem key={s.station_id} value={s.station_id}>{s.station_name}</MenuItem>
                ))}
              </Select>
              {formErrors.stationId && <Typography color="error" variant="caption">{formErrors.stationId}</Typography>}
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Start Date</Typography>
              <TextField type="date" fullWidth size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              {formErrors.startDate && <Typography color="error" variant="caption">{formErrors.startDate}</Typography>}
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">End Date</Typography>
              <TextField type="date" fullWidth size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              {formErrors.endDate && <Typography color="error" variant="caption">{formErrors.endDate}</Typography>}
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Frequency</Typography>
              <Select fullWidth size="small" value={frequency} onChange={(e) => setFrequency(Number(e.target.value))}>
                {FREQUENCY_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Nurse (optional)</Typography>
              <Select fullWidth size="small" value={nurseId || ''} onChange={(e) => setNurseId(e.target.value || '')} displayEmpty>
                <MenuItem value="">None</MenuItem>
                {nurses.map((n) => (
                  <MenuItem key={n.nurse_id} value={n.nurse_id}>{n.nurse_name}</MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">Start Time</Typography>
              <TextField type="time" fullWidth size="small" value={startTime ? startTime.slice(0, 5) : ''} onChange={(e) => setStartTime((e.target.value || '08:00') + ':00')} InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }} />
              {formErrors.startTime && <Typography color="error" variant="caption">{formErrors.startTime}</Typography>}
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">End Time</Typography>
              <TextField type="time" fullWidth size="small" value={endTime ? endTime.slice(0, 5) : ''} onChange={(e) => setEndTime((e.target.value || '20:00') + ':00')} InputLabelProps={{ shrink: true }} inputProps={{ step: 300 }} />
              {formErrors.endTime && <Typography color="error" variant="caption">{formErrors.endTime}</Typography>}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">Instruction</Typography>
              <TextField fullWidth size="small" placeholder="Enter Description within 50 character." value={instructions} onChange={(e) => setInstructions(e.target.value.slice(0, 50))} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={closeAddModal}>Cancel</Button>
          <Button variant="contained" onClick={handleAddTask}>Add Task</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editModalOpen} onClose={closeEditModal} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Task</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 0.5 }}>Select Vitals (add or remove)</Typography>
          <FormGroup row sx={{ flexWrap: 'wrap' }}>
            {vitalFields.map((f) => (
              <FormControlLabel
                key={f.field_name}
                control={<Checkbox checked={!!selectedVitals[f.field_name]} onChange={() => toggleVital(f.field_name)} />}
                label={f.label || f.field_name}
              />
            ))}
          </FormGroup>
          {formErrors.vitals && <Typography color="error" variant="caption">{formErrors.vitals}</Typography>}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={closeEditModal}>Cancel</Button>
          <Button variant="contained" onClick={handleEditTask}>Update</Button>
        </DialogActions>
      </Dialog>

      {deletePopup && (
        <DeletePopup
          DeletTitle="Delete this vital task?"
          deleteAlertPopupClose={closeDeletePopup}
          removeData={handleDeleteTask}
        />
      )}
      {isErrorMsg && <ToastMsg severity="error" msg={isErrorMsgText} msgPop={msgClose} />}
      {successMsg && <ToastMsg severity="success" msg={successMsgText} msgPop={msgClose} />}
      <Loader loaderOpen={isLoader} />
    </Box>
  );
}

export default VitalByTask;
